import { memo } from "react";
import {
  Badge,
  Box,
  CircularProgress,
  Divider,
  Paper,
  Stack,
  Switch,
  Typography,
} from "@mui/material";
import { MarkEmailUnreadOutlined, VideoCameraFront, VideocamOff } from "@mui/icons-material";
import dayjs from "dayjs";
import { InviteTaskActions } from "../../invites/components/InviteTaskActions";
import { invitePatientDisplay } from "../../invites/helpers/inviteTaskHelpers";
import { usePendingInviteTasks } from "../../invites/hooks/usePendingInviteTasks";
import { useTeleconsultoriaPresenceState } from "../../presence/TeleconsultoriaPresenceContext";

/** Convite compacto: paciente, quem pediu, prazo e Aceitar/Recusar. */
const CompactInvite = memo(function CompactInvite({ task }: { task: fhir5.Task }) {
  const deadline = task.restriction?.period?.end;
  return (
    <Stack spacing={0.75} role="listitem" sx={{ py: 1 }}>
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="subtitle2" fontWeight={700} noWrap>
          {invitePatientDisplay(task)}
        </Typography>
        <Typography variant="caption" color="text.secondary" component="p" noWrap>
          {task.requester?.display ? `De ${task.requester.display}` : "Convite recebido"}
          {deadline ? ` · até ${dayjs(deadline).format("DD/MM HH:mm")}` : ""}
        </Typography>
      </Box>
      <InviteTaskActions task={task} isCompact />
    </Stack>
  );
});

/**
 * Cartão único "Disponibilidade e convites" (prestadora, visão profissional):
 * - switch de disponibilidade (estado GLOBAL: o mesmo do botão do topo);
 * - convites pendentes (Tasks), com Aceitar/Recusar sem sair da tela.
 * Substitui o painel antigo, que também listava convites por Appointment.
 */
export const AvailabilityInvitesCard = memo(function AvailabilityInvitesCard() {
  const { canManageAvailability, isAvailable, isLoading, isSaving, setAvailable } =
    useTeleconsultoriaPresenceState();
  const pendingInvites = usePendingInviteTasks();
  const isBusy = isLoading || isSaving;

  return (
    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
      {canManageAvailability && (
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Box sx={{ display: "flex", color: isAvailable ? "success.main" : "text.disabled" }}>
            {isAvailable ? <VideoCameraFront /> : <VideocamOff />}
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="subtitle1" fontWeight={700}>
              {isAvailable ? "Disponível" : "Indisponível"}
            </Typography>
            <Typography variant="caption" color="text.secondary" component="p">
              {isAvailable
                ? "Você aparece online para convites e \"Atender agora\"."
                : "Ligue para receber convites e atendimentos agora."}
            </Typography>
          </Box>
          {isBusy ? (
            <CircularProgress size={22} aria-label="Salvando disponibilidade" />
          ) : (
            <Switch
              checked={isAvailable}
              onChange={(_, checked) => void setAvailable(checked)}
              inputProps={{ "aria-label": "Disponibilidade para teleconsultoria" }}
            />
          )}
        </Stack>
      )}

      {canManageAvailability && <Divider sx={{ my: 1.5 }} />}

      <Stack direction="row" spacing={1} alignItems="center">
        <Badge badgeContent={pendingInvites.length} color="warning">
          <MarkEmailUnreadOutlined color={pendingInvites.length ? "warning" : "disabled"} />
        </Badge>
        <Typography variant="subtitle2" fontWeight={700} sx={{ pl: 1 }}>
          Convites para responder
        </Typography>
      </Stack>
      {pendingInvites.length ? (
        <Stack role="list" divider={<Divider flexItem />} sx={{ mt: 0.5 }}>
          {pendingInvites.map((task) => (
            <CompactInvite key={task.id} task={task} />
          ))}
        </Stack>
      ) : (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Nenhum convite aguardando você.
        </Typography>
      )}
    </Paper>
  );
});
